import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans } from "react-i18next";

/**
 * Anchor-style navigation link used inside the top navbar.
 * - Detects the active route (exact match for "/" and prefix match otherwise)
 *   so the current page gets an animated underline and aria-current="page".
 * - Renders a real <button> for accessibility & keyboard navigation.
 */
export const NavbarButtonLink = ({ item, afterAction, sx, ...props }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const href = item?.href || "/";
    const cleanPath = href.split("#")[0] || "/";
    const isActive =
        cleanPath === "/"
            ? pathname === "/"
            : pathname === cleanPath || pathname.startsWith(`${cleanPath}/`);

    return (
        <Box
            component="button"
            type="button"
            aria-current={isActive ? "page" : undefined}
            onClick={() => {
                navigate(href);
                if (typeof afterAction === "function") afterAction();
            }}
            sx={{
                position: "relative",
                background: "transparent",
                border: "none",
                color: "common.white",
                cursor: "pointer",
                fontSize: "0.95rem",
                fontWeight: isActive ? 700 : 500,
                letterSpacing: 0.2,
                px: 1.25,
                py: 1,
                borderRadius: 1,
                transition: "color .15s ease, background-color .15s ease",
                "&:hover": {
                    bgcolor: (theme) => alpha("#fff", 0.08),
                },
                "&:focus-visible": {
                    outline: (theme) => `2px solid ${alpha("#fff", 0.7)}`,
                    outlineOffset: 2,
                },
                // Animated underline indicator
                "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "20%",
                    right: "20%",
                    bottom: 4,
                    height: 2,
                    borderRadius: 2,
                    bgcolor: "common.white",
                    transform: isActive ? "scaleX(1)" : "scaleX(0)",
                    transformOrigin: "center",
                    transition: "transform .2s ease",
                },
                "&:hover::after": { transform: "scaleX(1)" },
                ...sx,
            }}
            {...item?.props}
            {...props}
        >
            <Trans i18nKey={`component.navbar.${item?.trans}`} />
        </Box>
    );
};
